/**
 * TabGroup - Composant de navigation par onglets standardisé
 * Directive #4 : Source unique de vérité pour les styles de tabs
 * Design épuré avec labels toujours visibles pour meilleure discoverabilité
 */

import type { LucideIcon } from "lucide-react";

interface TabProps {
  value: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

function Tab({ value, icon: Icon, children, isActive, onClick }: TabProps) {
  return (
    <button
      onClick={onClick}
      className={`
        px-3 md:px-4 py-2.5 font-medium transition-all relative whitespace-nowrap
        flex items-center gap-2
        ${isActive
          ? "text-[var(--color-steel)] border-b-2 border-[var(--color-steel)] bg-[var(--color-steel-bg)]"
          : "text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--muted)] border-b-2 border-transparent"
        }
      `}
      title={typeof children === "string" ? children : undefined}
    >
      {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
      <span className="hidden sm:inline text-sm">{children}</span>
    </button>
  );
}

interface TabGroupProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  tabs: Array<{
    value: T;
    label: string;
    icon?: LucideIcon;
  }>;
  actions?: React.ReactNode;
}

export function TabGroup<T extends string>({ value, onChange, tabs, actions }: TabGroupProps<T>) {
  return (
    <div className="border-b border-[var(--border)]">
      <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto scrollbar-hide">
        {/* Tabs on left */}
        <div className="flex gap-0.5 flex-shrink-0">
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              icon={tab.icon}
              isActive={value === tab.value}
              onClick={() => onChange(tab.value)}
            >
              {tab.label}
            </Tab>
          ))}
        </div>

        {/* Action buttons on right - flex-shrink-0 pour éviter compression */}
        {actions && (
          <div className="flex gap-2 mb-0.5 ml-auto flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}